import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import { 
  LayoutDashboard, 
  Calendar, 
  User, 
  LogOut,
  BookOpen,
  BarChart3,
  ChevronDown,
  Settings,
  Edit,
  MessageCircle
} from 'lucide-react';

export default function MentorLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mentor, logout } = useMentorAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/mentor/login');
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { path: '/mentor/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/mentor/sessions', label: 'Mis Sesiones', icon: Calendar },
    { path: '/mentor/chat', label: 'Chat', icon: MessageCircle },
    { path: '/mentor/stats', label: 'Estadísticas', icon: BarChart3 },
    { path: '/mentor/profile', label: 'Mi Perfil', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex">
      {/* Sidebar */}
      <aside className="w-64 bg-black/50 backdrop-blur-xl border-r border-white/10 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_admin-portal-118/artifacts/77o33ulj_Recurso%2026LOGO%20def.png"
              alt="Logo"
              className="h-10 w-auto"
            />
            <div>
              <h2 className="text-lg font-bold text-white">Panel Mentor</h2>
              <p className="text-xs text-gray-400">Urpe Liberty MBA</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  active
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-white/10">
          {/* User Menu Toggle */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
            >
              {mentor?.photo_url ? (
                <img
                  src={mentor.photo_url}
                  alt={mentor.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-teal-500"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center border-2 border-teal-500">
                  <User size={20} className="text-teal-400" />
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-white truncate">{mentor?.name}</p>
                <p className="text-xs text-gray-400 truncate">{mentor?.email}</p>
              </div>
              <ChevronDown 
                size={18} 
                className={`text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl overflow-hidden">
                <Link
                  to="/mentor/profile"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-white/5 transition-colors text-gray-300 hover:text-white"
                >
                  <Edit size={18} />
                  <span className="text-sm">Editar Perfil</span>
                </Link>
                <Link
                  to="/mentor/security"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center space-x-3 px-4 py-3 hover:bg-white/5 transition-colors text-gray-300 hover:text-white"
                >
                  <Settings size={18} />
                  <span className="text-sm">Ajustes de Seguridad</span>
                </Link>
                <div className="border-t border-white/10"></div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-500/10 transition-colors text-red-400"
                >
                  <LogOut size={18} />
                  <span className="text-sm">Cerrar Sesión</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
